import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CANCEL_SALE_ORDER_MUTATION, CANCELLATION_REASONS_QUERY } from './cashier-pos.gql';

import { CANCELLATION_SCOPE_FROM_GQL } from '@/shared/graphql/enums';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { SecretInput } from '@/shared/ui/secret-input';
import { showToast } from '@/shared/ui/toast';

interface ReasonsResponse {
  cancellationReasons: {
    items: { id: string; code: string; description: string; scope: string }[];
  };
}

interface CancelSaleDialogProps {
  sale_order_id: string;
  on_close: () => void;
  on_done: () => void | Promise<void>;
  on_error: (msg: string | null) => void;
}

export function CancelSaleDialog({
  sale_order_id,
  on_close,
  on_done,
  on_error,
}: CancelSaleDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { data } = useQuery<ReasonsResponse>(CANCELLATION_REASONS_QUERY, {
    variables: { offset: 0, limit: 100 },
    fetchPolicy: 'cache-first',
  });
  const reasons = (data?.cancellationReasons.items ?? []).filter((reason) => {
    const scope =
      CANCELLATION_SCOPE_FROM_GQL[reason.scope as keyof typeof CANCELLATION_SCOPE_FROM_GQL] ??
      reason.scope;
    return scope === 'sale' || scope === 'order_return';
  });

  const [reason_id, setReasonId] = useState('');
  const [note, setNote] = useState('');
  const [otp_plaintext, setOtpPlaintext] = useState('');
  const [cancelSaleOrder, { loading }] = useMutation(CANCEL_SALE_ORDER_MUTATION);

  const can_submit = reason_id.length > 0 && otp_plaintext.trim().length > 0 && !loading;

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    on_error(null);
    const idempotency_key =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      await cancelSaleOrder({
        variables: {
          input: {
            sale_order_id,
            cancellation_reason_id: reason_id,
            note: note.trim() || null,
            otp_plaintext: otp_plaintext.trim(),
          },
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': idempotency_key } },
      });
      showToast({
        tone: 'success',
        title: t('cashier_pos.cancel_sale_success_title'),
        body: t('cashier_pos.cancel_sale_success_body'),
      });
      await on_done();
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code === 'otp_invalid') return on_error(t('otp.error_invalid'));
        if (code === 'otp_expired') return on_error(t('otp.error_expired'));
        if (code.startsWith('order_not_cancellable'))
          return on_error(t('cashier_pos.error_not_cancellable'));
        if (code.startsWith('sale_has_active_invoice'))
          return on_error(t('cashier_pos.error_sale_has_active_invoice'));
        if (code === 'open_account_movement_not_latest')
          return on_error(t('cashier_pos.error_open_account_movement_not_latest'));
        if (code === 'open_account_movement_not_found')
          return on_error(t('cashier_pos.error_open_account_movement_not_found'));
        return on_error(code);
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('cashier_pos.cancel_sale_title')}</CardTitle>
          <p className="text-muted-foreground text-xs">{t('cashier_pos.cancel_sale_hint')}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{t('cashier_pos.cancel_reason')}</Label>
            <select
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 text-sm"
              value={reason_id}
              onChange={(e) => setReasonId(e.target.value)}
              disabled={loading}
            >
              <option value="">{t('cashier_pos.cancel_reason_pick')}</option>
              {reasons.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.description}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>{t('cashier_pos.cancel_note')}</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} disabled={loading} />
          </div>
          <div className="space-y-1">
            <Label>{t('otp.code_label')}</Label>
            <SecretInput
              autoComplete="off"
              value={otp_plaintext}
              onChange={(e) => setOtpPlaintext(e.target.value)}
              disabled={loading}
              placeholder={t('otp.code_placeholder')}
            />
            <p className="text-muted-foreground text-xs">{t('otp.cancel_sale_hint')}</p>
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {loading ? t('common.loading') : t('cashier_pos.cancel_sale_confirm')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
